
export function projectSlider() {
    const sliders = document.querySelectorAll("[data-project-slider]");
    if (!sliders.length) return;

    const controller = new AbortController();
    const ticks = [];
    let direction = 1;
    let lastScrollY = window.scrollY;

    window.addEventListener("scroll", () => {
        const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const scrollY = Math.max(0, Math.min(window.scrollY, maxScrollY));
        const delta = scrollY - lastScrollY;

        // Ignore tiny settling movements and retain the last scroll direction.
        if (Math.abs(delta) < 1) return;

        direction = delta > 0 ? 1 : -1;
        lastScrollY = scrollY;
    }, { passive: true, signal: controller.signal });

    sliders.forEach((slider) => {
        const viewport = slider.querySelector(".projects-slider__viewport");
        const cards = [...slider.querySelectorAll(".project-card")];

        if (!viewport || !cards.length) return;


        let current = 0;
        let dragStartX = 0;
        let dragSensitivity = 1;
        let coastDuration = 325;
        let dragStartPosition = 0;
        let isDragging = false;
        let pointerId = null;
        let didDrag = false;
        let suppressClick = false;
        let isHovered = false;
        let hasKeyboardFocus = false;
        let autoplayStopped = false;
        let cardWidth = 0;
        let gap = 0;
        let lastDragX = 0;
        let lastDragTime = 0;
        let dragVelocity = 0;
        let coastVelocity = null;
        let needsRender = false;
        let activeCard = null;

        const count = cards.length;
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");


        /*
         * Kortste afstand tussen card en huidige positie.
         *
         * Bijvoorbeeld:
         * current = 5.8
         * card 0 wordt daardoor gezien als 0.2 verderop,
         * in plaats van 5.8 cards terug.
         */
        const getWrappedDistance = (index, position) => {
            const distance = index - position;

            return ((distance + count / 2) % count + count) % count - count / 2;
        };


        const normalizePosition = (position) =>
            ((position % count) + count) % count;


        const render = () => {
            const layout = cards.map((card, index) => {
                const distance = getWrappedDistance(index, current);
                const absoluteDistance = Math.abs(distance);


                /*
                 * Middelste card = 1
                 * buurcards = ±1
                 * buitenste cards = ±2
                 */
                const scale = absoluteDistance <= 1
                    ? 1 - absoluteDistance * 0.2
                    : gsap.utils.clamp(
                        0.6,
                        0.8,
                        0.8 - (absoluteDistance - 1) * 0.12
                    );


                /*
                 * Verder van het midden = iets donkerder.
                 */
                const opacity = gsap.utils.clamp(
                    0.55,
                    1,
                    1 - absoluteDistance * 0.12
                );

                return { card, distance, absoluteDistance, scale, opacity, x: 0 };
            }).sort((a, b) => a.distance - b.distance);

            const nextActiveCard = layout.reduce((closest, item) =>
                item.absoluteDistance < closest.absoluteDistance ? item : closest
            ).card;

            if (activeCard !== nextActiveCard) {
                activeCard?.classList.remove("is-active");
                nextActiveCard.classList.add("is-active");
                activeCard = nextActiveCard;
            }


            /*
             * Plaats iedere card naast de werkelijk geschaalde buurcard.
             * Zo blijft de zichtbare tussenruimte altijd exact var(--gutter).
             */
            for (let index = 1; index < layout.length; index += 1) {
                const previous = layout[index - 1];
                const item = layout[index];

                item.x = previous.x
                    + cardWidth * (previous.scale + item.scale) / 2
                    + gap;
            }


            const rightIndex = layout.findIndex((item) => item.distance >= 0);
            let centerOffset;

            if (rightIndex <= 0) {
                centerOffset = layout[Math.max(rightIndex, 0)].x;
            } else {
                const left = layout[rightIndex - 1];
                const right = layout[rightIndex];
                centerOffset = left.x + (right.x - left.x) * -left.distance;
            }


            layout.forEach(({ card, absoluteDistance, scale, opacity, x }) => {

                gsap.set(card, {
                    x: x - centerOffset,
                    y: 0,
                    xPercent: -50,
                    yPercent: -50,
                    scale,
                    opacity,
                    zIndex: Math.round(100 - absoluteDistance * 10)
                });
            });
        };


        // Continuous movement; keep the existing centered/scaled card layout.
        const tick = (_time, deltaTime) => {
            if (needsRender) {
                render();
                needsRender = false;
            }
            if (reducedMotion.matches || document.hidden) {
                coastVelocity = null;
                return;
            }
            if (count < 2 || isDragging) return;

            const elapsed = Math.min(deltaTime, 64);
            const targetVelocity = autoplayStopped || isHovered || hasKeyboardFocus ? 0 : direction / 4500;
            let movement = targetVelocity * elapsed;

            if (coastVelocity !== null) {
                // Frame-rate-independent friction, easing into autoplay (or rest on hover).
                const decay = Math.exp(-elapsed / coastDuration);
                const excessVelocity = coastVelocity - targetVelocity;
                movement += excessVelocity * coastDuration * (1 - decay);
                coastVelocity = targetVelocity + excessVelocity * decay;

                if (Math.abs(coastVelocity - targetVelocity) < 0.00001) {
                    coastVelocity = null;
                }
            }

            if (!movement) return;
            current = normalizePosition(current + movement);
            render();
        };

        viewport.addEventListener("pointerdown", (event) => {
            if (!event.isPrimary || event.button !== 0) return;

            if (event.pointerType === "touch") autoplayStopped = true;

            pointerId = event.pointerId;
            isDragging = true;
            hasKeyboardFocus = false;
            didDrag = false;
            suppressClick = false;
            dragStartX = event.clientX;
            dragSensitivity = event.pointerType === "touch" ? 1.7 : 1;
            coastDuration = event.pointerType === "touch" ? 450 : 325;
            dragStartPosition = current;
            lastDragX = event.clientX;
            lastDragTime = event.timeStamp;
            dragVelocity = 0;
            coastVelocity = null;
        });

        viewport.addEventListener("pointermove", (event) => {
            if (!isDragging || event.pointerId !== pointerId) return;

            const delta = event.clientX - dragStartX;
            if (!didDrag) {
                const threshold = event.pointerType === "touch" ? 5 : 8;
                if (Math.abs(delta) < threshold) return;
            }

            // Capture only real drags so a normal click still reaches the link.
            if (!didDrag) {
                didDrag = true;
                viewport.setPointerCapture(pointerId);
                viewport.classList.add("is-dragging");
            }

            const elapsed = Math.max(1, event.timeStamp - lastDragTime);
            const velocity = -(event.clientX - lastDragX) * dragSensitivity / (cardWidth || 1) / elapsed;
            const blend = 1 - Math.exp(-elapsed / 40);
            dragVelocity += (velocity - dragVelocity) * blend;
            dragVelocity = gsap.utils.clamp(-0.004, 0.004, dragVelocity);
            lastDragX = event.clientX;
            lastDragTime = event.timeStamp;

            current = normalizePosition(dragStartPosition - delta * dragSensitivity / (cardWidth || 1));
            needsRender = true;
        });

        const endDrag = (event) => {
            if (!isDragging || event.pointerId !== pointerId) return;

            isDragging = false;
            suppressClick = didDrag;
            // Holding still before release should stop the throw; cancelled gestures never coast.
            const releaseDelay = Math.max(0, event.timeStamp - lastDragTime);
            coastVelocity = didDrag && event.type === "pointerup" &&
                !reducedMotion.matches && releaseDelay < 120
                ? dragVelocity * Math.exp(-releaseDelay / 80)
                : null;
            viewport.classList.remove("is-dragging");

            if (viewport.hasPointerCapture(pointerId)) {
                viewport.releasePointerCapture(pointerId);
            }
            pointerId = null;
        };

        window.addEventListener("pointerup", endDrag, { signal: controller.signal });
        window.addEventListener("pointercancel", endDrag, { signal: controller.signal });
        viewport.addEventListener("lostpointercapture", (event) => {
            // Child links lose implicit touch capture when the viewport takes over.
            if (event.target === viewport) endDrag(event);
        });
        viewport.addEventListener("dragstart", (event) => event.preventDefault());
        viewport.addEventListener("click", (event) => {
            if (!suppressClick || event.detail === 0) return;

            event.preventDefault();
            event.stopPropagation();
            suppressClick = false;
        }, true);

        cards.forEach((card) => {
            card.addEventListener("pointerenter", (event) => {
                if (event.pointerType === "mouse") isHovered = true;
            });
            card.addEventListener("pointerleave", () => {
                isHovered = false;
            });
        });
        viewport.addEventListener("focusin", (event) => {
            // Pointer focus must not keep autoplay paused after the mouse leaves.
            hasKeyboardFocus = !isDragging && event.target.matches(":focus-visible");
            const index = cards.indexOf(event.target.closest(".project-card"));
            // Reveal off-screen links when navigating with the keyboard.
            if (index >= 0 && hasKeyboardFocus) {
                coastVelocity = null;
                current = index;
                render();
            }
        });
        viewport.addEventListener("focusout", () => {
            hasKeyboardFocus = false;
        });

        const resize = () => {
            if (controller.signal.aborted) return;
            cardWidth = cards[0].offsetWidth;
            gap = parseFloat(getComputedStyle(viewport).columnGap) || 0;
            const captionHeight = Math.max(...cards.map((card) =>
                card.querySelector(".project-card__content")?.offsetHeight || 0
            ));
            viewport.style.setProperty("--project-caption-height", `${captionHeight}px`);
            render();
        };

        window.addEventListener("resize", resize, { signal: controller.signal });
        resize();
        document.fonts.ready.then(resize);
        gsap.ticker.add(tick);
        ticks.push(tick);
    });
    return () => {
        controller.abort();
        ticks.forEach((tick) => gsap.ticker.remove(tick));
    };
}
